import { compact, flatMap, isEmpty, map } from "lodash/fp";

import type {
  ConferencingMeetingPlatform,
  IntegrationProviderId,
} from "@readai/common/types";
import type {
  ApiDateRangeSearchFilter,
  ApiEnterpriseSearchAIAnswer,
  ApiEnterpriseSearchRequestBody,
  ApiEnterpriseSearchResponse,
  ApiEnterpriseSearchResultItem,
  ApiEnterpriseSearchResultItemSource,
  ApiEnterpriseSearchSourceProvider,
  ApiSharedSearchesResponse,
  ApiSharedSearchResponse,
  ApiValueSearchFilter,
  EnterpriseAggregatedSearchResultItem,
  EnterpriseSearchConferenceProvider,
  EnterpriseSearchDateFilter,
  EnterpriseSearchMessagingProvider,
  EnterpriseSearchQueryRequest,
  EnterpriseSearchResponse,
  EnterpriseSearchResponseAiAnswer,
  EnterpriseSearchResultItem,
  EnterpriseSearchResultItemSource,
  EnterpriseSearchSourceProvider,
  SharedSearch,
  SharedSearches,
} from "@readai/common/types/enterpriseSearchTypes";
import { parseUTCDateTimeFromString } from "@readai/common/utils/datetimeUtils";

const makeDateRangeFilter = (
  dateFilter: EnterpriseSearchDateFilter | null,
): ApiDateRangeSearchFilter | null => {
  if (!dateFilter) return null;

  const [rangeStart, rangeEnd] = dateFilter.range;

  return {
    kind: "date_range",
    field: "start_time",
    criteria: {
      start: rangeStart.startOf("day").toUTC().toISO()!,
      end: rangeEnd.endOf("day").toUTC().toISO()!,
    },
  };
};

const PROVIDER_TO_API_PROVIDERS: {
  [key in EnterpriseSearchSourceProvider]?: ApiEnterpriseSearchSourceProvider[];
} = {
  confluence: ["confluence"],
  notion: ["notion"],
  google_docs: ["google_docs"],
  jira: ["jira"],
  meet: ["meet"],
  mobile: ["mobile"],
  teams: ["teams", "teams_web"],
  webex: ["webex"],
  zoom: ["zoom"],
  google_email: ["gmail"],
  microsoft_graph_email: ["outlook"],
  microsoft_graph_teams_chat_ingest: ["teams_chat", "teams_channel"],
  slack_ingest: ["slack"],
};

const makeSourceFilter = (
  sources: EnterpriseSearchSourceProvider[] | null,
): ApiValueSearchFilter | null => {
  if (!sources || isEmpty(sources)) return null;

  const apiSources = compact(
    flatMap((s) => PROVIDER_TO_API_PROVIDERS[s], sources),
  );

  return {
    kind: "value",
    field: "source.provider",
    criteria: {
      values: apiSources,
    },
  };
};

const makeSessionFilters = (sessionId?: string): ApiValueSearchFilter[] => {
  if (!sessionId) return [];

  return [
    {
      kind: "value",
      field: "source.id",
      criteria: {
        values: [sessionId],
      },
    },
    {
      kind: "value",
      field: "source.kind",
      criteria: { values: ["session"] },
    },
  ];
};

export const transformRequest = (
  request: EnterpriseSearchQueryRequest,
): ApiEnterpriseSearchRequestBody => {
  const body: ApiEnterpriseSearchRequestBody = {
    userId: request.userId,
    workspaceId: request.workspaceId,
    query: request.query,
  };

  const filters = compact([
    makeDateRangeFilter(request.filters.dates),
    makeSourceFilter(request.filters.sources),
    ...makeSessionFilters(request.sessionId),
  ]);

  if (!isEmpty(filters)) {
    body.filters = filters;
  }

  return body;
};

// TODO (ENG-14310) - we have similar mappings for topic reports. could
//  probably update both places to use a shared util that simply maps
//  metrics provider ID -> BE/FE provider ID
const CONFERENCE_PROVIDER_TO_MEETING_PLATFORM: {
  [key in EnterpriseSearchConferenceProvider]: ConferencingMeetingPlatform;
} = {
  meet: "meet",
  mobile: "mobile",
  teams: "teams",
  teams_web: "teams",
  webex: "webex",
  zoom: "zoom",
};

const MESSAGING_PROVIDER_TO_INTEGRATION_PROVIDER: {
  [key in EnterpriseSearchMessagingProvider]: IntegrationProviderId;
} = {
  gmail: "google_email",
  outlook: "microsoft_graph_email",
  teams_chat: "microsoft_graph_teams_chat_ingest",
  teams_channel: "microsoft_graph_teams_chat_ingest",
  slack: "slack_ingest",
};

const parseSource = (
  source: ApiEnterpriseSearchResultItemSource,
): EnterpriseSearchResultItemSource => {
  if (source.providerKind === "document" || source.providerKind === "crm") {
    return {
      ...source,
      provider: source.provider,
    };
  }

  if (source.providerKind === "conference") {
    return {
      ...source,
      provider: CONFERENCE_PROVIDER_TO_MEETING_PLATFORM[source.provider],
    };
  }

  return {
    ...source,
    provider: MESSAGING_PROVIDER_TO_INTEGRATION_PROVIDER[source.provider],
  };
};

const parseResultItems = (
  item: ApiEnterpriseSearchResultItem,
): EnterpriseSearchResultItem => {
  const source = parseSource(item.source);
  const title =
    source.provider === "slack_ingest" ? `#${item.title}` : item.title;
  return {
    ...item,
    startTime: parseUTCDateTimeFromString(item.startTime),
    endTime: parseUTCDateTimeFromString(item.endTime),
    source,
    title,
  };
};

const parseAggregatedResultItems = (
  items?: ApiEnterpriseSearchResultItem[],
  objectGroups?: number[][],
): EnterpriseAggregatedSearchResultItem[] => {
  if (!items || !objectGroups) {
    return [];
  }
  return objectGroups
    ?.filter((group) => group.length) // ensure group is not empty
    ?.map((group, objectGroupIndex) => {
      const item = items[group[0]];
      const source = parseSource(item.source);
      const title =
        source.provider === "slack_ingest" ? `#${item.title}` : item.title;
      const descriptions = group.map((i) => items[i].description);
      return {
        id: item.id,
        objectGroupIndex,
        kind: item?.kind,
        title,
        descriptions: descriptions,
        startTime: parseUTCDateTimeFromString(item.startTime),
        endTime: parseUTCDateTimeFromString(item.endTime),
        source,
        metadata: item.metadata,
        actors: item.actors,
        links: item.links,
      };
    });
};

const parseAiAnswer = (
  aiAnswer: ApiEnterpriseSearchAIAnswer,
  id: string,
): EnterpriseSearchResponseAiAnswer => ({
  ...aiAnswer,
  id,
  citations: map(parseResultItems, aiAnswer.citations),
});

export const parseResponse = (
  response?: ApiEnterpriseSearchResponse,
): EnterpriseSearchResponse => ({
  ...response,
  aiAnswer: response?.aiAnswer
    ? parseAiAnswer(response.aiAnswer, response.searchRequestId)
    : undefined,
  results: parseAggregatedResultItems(
    response?.objects,
    response?.objectGroups,
  ),
});

export const parseSharedSearch = (
  response: ApiSharedSearchResponse,
): SharedSearch => ({
  ...response,
  query: response.query || response.data.query || "",
  createdOn: parseUTCDateTimeFromString(response.createdOn),
  aiAnswer: response.data.aiAnswer
    ? parseAiAnswer(response.data.aiAnswer, "")
    : undefined,
});

export const parseSharedSearches = ({
  results = [],
  totalCount = 0,
  hasMore = false,
  limit,
  offset,
}: ApiSharedSearchesResponse): SharedSearches => ({
  results: results.map(parseSharedSearch),
  pagination: {
    totalCount,
    hasMore,
    limit,
    offset,
  },
});
